import {
  Component,
  OnDestroy,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
} from '@angular/core';
import { Router } from '@angular/router';
import { AgGridAngular } from 'ag-grid-angular';
import { AsyncPipe } from '@angular/common';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
// Ag-Grid
import {
  GridOptions,
  GridApi,
  ColDef,
  RowSelectionOptions,
  CellContextMenuEvent,
} from 'ag-grid-community';
import { agGridDarkTheme } from 'src/app/shared/components/ag-grid/dark-theme';
import { Subscription } from 'rxjs';
import { BsModalService, BsModalRef, ModalOptions } from 'ngx-bootstrap/modal';
// models
import { FFLManagementColumnDef } from './columnDefs';
import { Email, FFL } from 'src/app/shared/models/general';
import {
  Answers,
  ComponentNames,
  EmailType,
  ModalID,
  ModalType,
  Questions,
  StatusModalIndicator,
} from 'src/app/shared/models/constants';
//components
import { DispoEditBtnsComponent } from '../../shared/components/ag-grid/dispo-edit-btns/dispo-edit-btns.component';
import { FflManagementEditComponent } from './modals/ffl-management-edit/ffl-management-edit.component';
import { SharedService } from '../../shared/services/shared.service';
import { AgGridService } from 'src/app/shared/services/ag-grid.service';
import { ContextMenuService } from '../../shared/services/context-menu.service';
import { isObjectEmpty } from 'src/app/shared/functions';

@Component({
  selector: 'app-ffl-management',
  templateUrl: './ffl-management.component.html',
  styleUrls: ['./ffl-management.component.scss'],
  standalone: true,
  imports: [AgGridAngular, AsyncPipe, BsDropdownModule],
})
export class FflManagementComponent implements OnDestroy {
  subscriptions = new Subscription();
  rowData: FFL[] = [];
  @ViewChild('grid', { read: ElementRef }) gridElement!: ElementRef;
  @ViewChild('grid') grid!: AgGridAngular;
  menuState$ = this.contextMenuService.menuState$;

  public rowSelection: RowSelectionOptions = {
    mode: 'singleRow',
    enableSelectionWithoutKeys: true,
    checkboxes: false,
  };
  // ag-grid properties Grid
  gridApi!: GridApi;

  columnDefs: ColDef[] = FFLManagementColumnDef;
  defaultColDef;
  detailCellRendererParams: any;
  overlayLoadingTemplate;
  overlayNoRowsTemplate;
  gridOptions: GridOptions = {
    theme: agGridDarkTheme,
    suppressDragLeaveHidesColumns: true,
  };
  components: any;
  theme = agGridDarkTheme;

  modalRef: BsModalRef;
  modalConfig = {
    animated: true,
  };

  constructor(
    private router: Router,
    public sharedService: SharedService,
    private modalService: BsModalService,
    private agGridService: AgGridService,
    private contextMenuService: ContextMenuService,
    private cdr: ChangeDetectorRef,
  ) {
    this.overlayLoadingTemplate =
      '<span style="padding: 10px; border: 2px solid #444;background: #2399e5; color: white;"><i class="fa fa-spinner fa-spin"></i> Finding Disposition Records...</span>';
    this.overlayNoRowsTemplate =
      '<span style="padding: 10px; border: 2px solid #444;background: #2399e5; color: white;"><i class="fa fa-spinner fa-spin"></i> No Records Found!</span>';
    this.gridOptions = <GridOptions>{};
    this.gridOptions.headerHeight = 50;
    this.gridOptions.rowHeight = 25;
    this.gridOptions.enableCellTextSelection = true; // Allows text selection for copying
    this.gridOptions.ensureDomOrder = true; // Ensures proper DOM order for accessibility
    this.gridOptions.suppressClipboardPaste = false; // Allow clipboard operations
    this.gridOptions.context = { ConfigComponent: this }; // this allows access to component functions
    this.gridOptions.rowClass = 'ag-grid-row';
    this.defaultColDef = {
      sortable: true,
      filter: true,
      resizable: true,
      floatingFilter: true,
      wrapHeaderText: true,
    };
    this.rowSelection = {
      mode: 'singleRow',
      enableSelectionWithoutKeys: true,
      checkboxes: false,
    };
    this.components = {
      editDeleteBtns: DispoEditBtnsComponent,
    };
    // ffl_list_retrieved$
    this.subscriptions.add(
      this.sharedService.ffl_list_retrieved$.subscribe((response) => {
        this.rowData = this.sharedService.ffl_list;
      }),
    );
    // yes_no_submit$
    this.subscriptions.add(
      this.sharedService.yes_no_submit$.subscribe((response) => {
        if (
          response['answer'] === Answers.yes &&
          response['question'] === Questions.delete_this_ffl
        ) {
          this.sharedService.deleteFFL(response['optional_details'] as FFL);
        }
      }),
    );
    // ffl_to_add$
    this.subscriptions.add(
      this.sharedService.ffl_to_add$.subscribe((ffl) => {
        if (ffl.modal_type === ModalType.add) {
          this.addFFL(ffl.form_values, ffl.certified_ffl_pdf_file);
        } else {
          this.editFFL(ffl.form_values, ffl.certified_ffl_pdf_file, ffl.remove_certified_ffl_pdf);
        }
      }),
    );
    // ffl_list_updated$
    this.subscriptions.add(
      this.sharedService.ffl_list_updated$.subscribe((ffl_list) => {
        this.rowData = ffl_list;
      }),
    );
  }

  async onGridReady(params: any) {
    this.gridApi = params.api;

    if (!this.agGridService.hasCachedSettingsFor(ComponentNames.ffl_management)) {
      await this.agGridService.loadSavedTableSettings(
        ComponentNames.ffl_management,
      );
    }
    this.applyCurrentSessionPreferences();
  }

  ngAfterViewInit() {
    this.contextMenuService.initialize(this.cdr, this.gridElement);
  }
  ngOnInit(): void {
    this.addButtonColumn();
    if (this.sharedService.ffl_list.length > 0) {
      this.rowData = [];
      this.rowData = this.sharedService.ffl_list;
    } else {
      this.refreshTableDataClick();
      this.rowData = [...this.sharedService.ffl_list];
    }
  }
  addButtonColumn() {
    const hasEditDelete = this.columnDefs.some(
      (column) => column.headerName === 'Edit / Delete',
    );
    if (!hasEditDelete) {
      this.columnDefs.push({
        // Edit / Delete
        headerName: 'Edit / Delete',
        field: 'edit_delete',
        resizable: true,
        width: 140,
        minWidth: 140,
        pinned: 'right',
        suppressMovable: true,
        cellRenderer: 'editDeleteBtns',
        cellRendererParams: {
          editFirearm: this.editFFLClicked.bind(this),
          deleteFirearm: this.deleteFFLClicked.bind(this),
        },
      });
    }
  }
  async addFFL(new_ffl: FFL, file?: File) {
    await this.sharedService.addFFLToAvailabilityList(new_ffl);
    if (file && this.sharedService.networkSync.isOnline) {
      const serverFfl = this.sharedService.ffl_list.find(f => f.ffl === new_ffl.ffl);
      if (serverFfl && serverFfl.id) {
        try {
          await this.sharedService.uploadCertifiedFflPdf(serverFfl.id, file);
          await this.sharedService.getFFLList();
        } catch (e) {
          this.sharedService.raiseErrorToast('Failed to upload Certified FFL PDF.');
        }
      }
    } else if (file && !this.sharedService.networkSync.isOnline) {
      this.sharedService.raiseWarningToast('Offline: Cannot upload PDF currently.');
    }
  }
  async editFFL(ffl_to_edit: FFL, file?: File, removePdf?: boolean) {
    await this.sharedService.editFFL(ffl_to_edit);
    if (file && ffl_to_edit.id && this.sharedService.networkSync.isOnline) {
      try {
        await this.sharedService.uploadCertifiedFflPdf(ffl_to_edit.id, file);
        await this.sharedService.getFFLList();
      } catch (e) {
        this.sharedService.raiseErrorToast('Failed to upload Certified FFL PDF.');
      }
    } else if (removePdf && ffl_to_edit.id && this.sharedService.networkSync.isOnline) {
      try {
        await this.sharedService.deleteCertifiedFflPdf(ffl_to_edit.id);
        await this.sharedService.getFFLList();
      } catch (e) {
        this.sharedService.raiseErrorToast('Failed to remove Certified FFL PDF.');
      }
    } else if ((file || removePdf) && !this.sharedService.networkSync.isOnline) {
      this.sharedService.raiseWarningToast('Offline: Cannot manage PDF currently.');
    }
  }
  // button click functions
  editFFLClicked(selected_row: { colValues: FFL; rowIndex: number }) {
    this.addEditFFLClick(ModalType.edit, selected_row['colValues']);
  }
  deleteFFLClicked(selected_row: { colValues: FFL; rowIndex: number }) {
    this.sharedService.openYesNoModal(
      Questions.delete_this_ffl,
      selected_row['colValues'],
    );
  }
  async viewAttachment(fflId: string) {
    try {
      const url = await this.sharedService.getCertifiedFflPdfUrl(fflId);
      window.open(url, '_blank');
    } catch (e) {
      this.sharedService.raiseErrorToast('Could not load PDF link.');
    }
  }
  async refreshTableDataClick() {
    this.sharedService.openStatusSpinner(`Loading Available FFL's`);
    await this.sharedService.getFFLList();
    this.sharedService.closeStatusSpinner();
  }
  addEditFFLClick(modal_type: string, row_data?: FFL) {
    const modalOptions: ModalOptions = {
      initialState: {
        modal_type: modal_type,
        selected_row: modal_type === ModalType.edit ? row_data : null,
      } as any,
      ignoreBackdropClick: true,
      // backdrop: 'static',
      keyboard: false,
      class: 'modal-dialog, modal-dialog-centered, modal-lg',
    };
    this.modalRef = this.modalService.show(
      FflManagementEditComponent,
      Object.assign({ id: ModalID.firearm_management_edit }, modalOptions),
    );
  }
  openHome() {
    this.router.navigate(['home']);
  }

  // right click copy functions
  onCellContextMenu(event: CellContextMenuEvent) {
    this.contextMenuService.onCellContextMenu(event);
  }
  onMenuMouseEnter() {
    this.contextMenuService.onMenuMouseEnter();
  }
  onMenuMouseLeave() {
    this.contextMenuService.onMenuMouseLeave();
  }
  copyCellValue() {
    this.contextMenuService.copyCellValue();
  }
  // preference functions
  storeCurrentSessionFilters() {
    this.agGridService.storeCurrentSessionFilters(
      ComponentNames.ffl_management,
      this.gridApi.getFilterModel(),
    );
  }
  storeCurrentSessionColumnConfig() {
    this.agGridService.storeCurrentSessionColumnConfig(
      ComponentNames.ffl_management,
      this.gridApi?.getColumnState(),
    );
  }
  applyCurrentSessionPreferences() {
    let component: any = this.agGridService.current_session_filtering.find(
      (component) => component.component_name === ComponentNames.ffl_management,
    );
    if (component && !isObjectEmpty(component.filters)) {
      this.gridApi.setFilterModel(component.filters);
    }
    component = this.agGridService.current_session_column_config.find(
      (component) => component.component_name === ComponentNames.ffl_management,
    );
    if (component && !isObjectEmpty(component.column_config)) {
      this.gridApi?.applyColumnState({
        state: component.column_config,
        applyOrder: true,
      });
    }
  }
  saveCurrentTablePreferencesClick() {
    const filters = this.gridApi.getFilterModel();
    const column_state = this.gridApi?.getColumnState();
    this.agGridService.saveCurrentTableSettings(
      ComponentNames.ffl_management,
      filters,
      column_state,
    );
  }
  RequestNewFFL(to_email: string) {
    const email = new Email();
    email.to = to_email;
    email.subject =
      this.sharedService.company_info.email_templates['ffl_request']['subject'];
    email.body =
      this.sharedService.company_info.email_templates['ffl_request']['body'];
    const email_type = EmailType.ffl;
    this.sharedService.openEmailModal(email_type, email);
  }
  isExpired(dateString: string | null | undefined): boolean {
    if (!dateString) return false;
    const date = new Date(dateString);
    return date < new Date();
  }
  requestFFLFromContextMenu(rowData: any) {
    if (!rowData || !rowData.email) {
      this.sharedService.raiseErrorToast(
        'No email address found for this FFL.',
      );
      return;
    }
    this.RequestNewFFL(rowData.email);
  }
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
